import { ArrowRight, Flex, HelpIcon, Rocket, Text, useTooltip, WarningIcon } from 'vvs-uikit'
import React, { useMemo, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import { useSetState } from 'react-use'
import { useTranslation } from 'contexts/Localization'
import { PairDetail, TokenPair } from './type'
import { OptionCard } from './OptionCard'

type Id = number
export type Option = {
  id: Id
  tokenPair: TokenPair
  details?: PairDetail[]
  lpToken: string
  vvsRewardsApr?: number
  tokenRewardsApr?: number
  lpRewardsApr?: number
  version?: number
}
type Props = {
  options: Option[]
  defaultValues?: Id[]
  onSelect?: (ids: Id[]) => void
  limit?: number
}

type DetailBoostAprProps = {
  from: number
  to: number
}

export const TokenPairSelector = ({ options, onSelect, limit, defaultValues = [] }: Props) => {
  const [selectionMap, setSelectionMap] = useSetState(
    Object.fromEntries(options.map((opt) => [opt.id, defaultValues.includes(opt.id)])),
  )

  const selected = useMemo(() => {
    return Object.entries(selectionMap)
      .filter(([, checked]) => checked)
      .map(([id]) => Number(id))
  }, [selectionMap])

  useEffect(() => {
    onSelect?.(selected)
  }, [onSelect, selected])

  return (
    <FlexOptions flexDirection="column">
      {options.map((opt) => {
        return (
          <OptionCard
            key={opt.id}
            pair={opt.tokenPair}
            details={opt.details}
            selected={selectionMap[opt.id]}
            disabled={limit && selected.length === limit && !selectionMap[opt.id]}
            onChange={(checked: boolean) => {
              setSelectionMap({ [opt.id]: checked })
            }}
          />
        )
      })}
    </FlexOptions>
  )
}
const DetailBoostApr = ({ from, to }: DetailBoostAprProps) => {
  const { t } = useTranslation()
  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(
      'Max APR can be achieved by locking xVVS (up to max 2.5x) and earn additional boost factor by staking NFTs (up to max 3x).',
    ),
    {
      placement: 'top',
    },
  )
  const { colors } = useTheme()
  return (
    <Text>
      <Flex columnGap="4px" alignItems="center">
        <Rocket width={16} height={16} color={colors.text} />
        {`${(from * 100).toFixed(1)}%`}
        <ArrowRight width={16} height={16} color={colors.text} />
        {`${(to * 100).toFixed(1)}%`}

        <Flex ref={targetRef} ml="6px">
          {/* hard code size 19.2 to render actual size of 16 */}
          <HelpIcon fill={colors.primary} width={19.2} height={19.2} />
          {tooltipVisible && tooltip}
        </Flex>
      </Flex>
    </Text>
  )
}

const WarnMsg: React.FC = ({ children }) => {
  const { colors } = useTheme()
  const { t } = useTranslation()
  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t('You have no liquidity inside this pool. Stake liquidity to enjoy boost APR.'),
    {
      placement: 'top',
    },
  )
  return (
    <Flex marginTop="8px" columnGap="4px">
      <Text>{children}</Text>
      <Flex ref={targetRef} ml="6px">
        <WarningIcon color={colors.warningLight} width="24px" height="24px" />
        {tooltipVisible && tooltip}
      </Flex>
    </Flex>
  )
}

TokenPairSelector.DetailBoostApr = DetailBoostApr
TokenPairSelector.WarnMsg = WarnMsg

const FlexOptions = styled(Flex)`
  > :not(:last-child) {
    border-bottom: 2px solid ${({ theme }) => theme.colors.lightBeige};
  }
`
